import { useMemo } from 'react'
import { useIntl } from 'react-intl'
import { CheckoutPageId } from 'checkout/constants/pages'
import productLabels from 'i18n/productLabels'
import { LUXURY_PLUS } from 'luxPlus/constants/base'

export const HOTEL_FLIGHTS_STEP_ROUTE_KEYS = [
  CheckoutPageId.AddFlights,
  CheckoutPageId.DepartingFlights,
  CheckoutPageId.ReturningFlights,
] as const

interface BuildHotelCheckoutStepsOptions {
  hasAccommodationItems: boolean
  currentRoute?: string
  isLuxPlusStepAvailable: boolean
  isFlightsStepAvailable: boolean
  isAirportTransfersStepAvailable?: boolean
  isExperiencesStepAvailable: boolean
  hasLuxPlusCheckoutItems: boolean
  hasFlightItems: boolean
  hasTransferItems?: boolean
  hasExperienceItems: boolean
  shouldShowFlightsBeforeLuxPlusUpsell: boolean
  currentRegionHasFlights?: boolean
}

export function isHotelFlightsRoute(stepKey?: string): boolean {
  return !!stepKey && HOTEL_FLIGHTS_STEP_ROUTE_KEYS.includes(stepKey as typeof HOTEL_FLIGHTS_STEP_ROUTE_KEYS[number])
}

export function useHotelCheckoutStepLabels(): Record<string, string> {
  const intl = useIntl()

  return useMemo(() => ({
    [LUXURY_PLUS.PROGRAM_NAME]: intl.formatMessage(productLabels.luxPlus),
    Flights: intl.formatMessage(productLabels.flights),
    Transfers: intl.formatMessage({
      id: 'kuooGfixhqSIptTPNOdM',
      defaultMessage: 'Transfers',
      description: 'Checkout progress step label for selecting airport transfers before final review',
    }),
    Experiences: intl.formatMessage(productLabels.experiences),
    'Review & Pay': intl.formatMessage(productLabels.reviewAndPay),
  }), [intl])
}

export const HOTEL_CHECKOUT_HEADER_STEPS: Record<'luxPlus' | 'flights' | 'airportTransfers' | 'experiences' | 'purchase', App.Checkout.CheckoutStep> = {
  luxPlus: {
    label: LUXURY_PLUS.PROGRAM_NAME,
    key: CheckoutPageId.LuxPlus,
    subSteps: [],
    isRoute: true,
  },
  flights: {
    label: 'Flights',
    key: CheckoutPageId.AddFlights,
    subSteps: HOTEL_FLIGHTS_STEP_ROUTE_KEYS.map(stepKey => ({
      label: 'Flights',
      key: stepKey,
      isRoute: true,
    })),
    isRoute: true,
  },
  airportTransfers: {
    label: 'Transfers',
    key: CheckoutPageId.AirportTransfers,
    subSteps: [],
    isRoute: true,
  },
  experiences: {
    label: 'Experiences',
    key: CheckoutPageId.Experiences,
    subSteps: [],
    isRoute: true,
  },
  purchase: {
    label: 'Review & Pay',
    key: CheckoutPageId.Purchase,
    subSteps: [],
    isRoute: true,
  },
}

export function buildHotelCheckoutSteps({
  hasAccommodationItems,
  currentRoute,
  isLuxPlusStepAvailable,
  isFlightsStepAvailable,
  isAirportTransfersStepAvailable = false,
  isExperiencesStepAvailable,
  hasLuxPlusCheckoutItems,
  hasFlightItems,
  hasTransferItems = false,
  hasExperienceItems,
  shouldShowFlightsBeforeLuxPlusUpsell,
  currentRegionHasFlights = true,
}: BuildHotelCheckoutStepsOptions): Array<App.Checkout.CheckoutStep> {
  if (!hasAccommodationItems) {
    return []
  }

  const steps: Array<App.Checkout.CheckoutStep> = []

  const shouldShowLuxPlusStep = currentRoute === CheckoutPageId.LuxPlus ||
    (isLuxPlusStepAvailable && !hasLuxPlusCheckoutItems)
  const shouldShowFlightsStep = currentRegionHasFlights && (
    isFlightsStepAvailable || isHotelFlightsRoute(currentRoute) || hasFlightItems
  )

  if (!shouldShowFlightsBeforeLuxPlusUpsell && shouldShowLuxPlusStep) {
    steps.push(HOTEL_CHECKOUT_HEADER_STEPS.luxPlus)
  }

  if (shouldShowFlightsStep) {
    steps.push(HOTEL_CHECKOUT_HEADER_STEPS.flights)
  }

  if (shouldShowFlightsBeforeLuxPlusUpsell && shouldShowLuxPlusStep) {
    steps.push(HOTEL_CHECKOUT_HEADER_STEPS.luxPlus)
  }

  if (isExperiencesStepAvailable || currentRoute === CheckoutPageId.Experiences || hasExperienceItems) {
    steps.push(HOTEL_CHECKOUT_HEADER_STEPS.experiences)
  }

  if (isAirportTransfersStepAvailable || currentRoute === CheckoutPageId.AirportTransfers || hasTransferItems) {
    steps.push(HOTEL_CHECKOUT_HEADER_STEPS.airportTransfers)
  }

  steps.push(HOTEL_CHECKOUT_HEADER_STEPS.purchase)

  return steps
}
